import type {
  IDataTableColDef,
  IDataTableRowClickedEvent,
} from '@kognitos/lattice';
import type { V1Run } from '@/generated/hooks/jeeves/models';
import type { V1BatchGetRunExecutionTimesResponseRunExecutionTimes } from '@/generated/hooks/bishop/models';
import {
  StatusCellRenderer,
  createTimeCellRenderer,
  createActionsCellRenderer,
  createInputsCellRenderer,
  createOutputCellRenderer,
  NoOutputsCellRenderer,
  OutputColumnHeader,
  TriggerCellRenderer,
  VersionCellRenderer,
} from './components/AutomationRunsListCells';
import { formatDisplayName } from '@/shared/utils';
import i18n from '@/shared/i18n';
import {
  OUTPUT_COLUMN_MAX_WIDTH,
  OUTPUT_COLUMN_MIN_WIDTH,
  RUNS_TABLE_FIXED_COLUMN_IDS,
} from './runs-table-widths';
import type { TResolvedOutputs, TResolvedValue } from '@/modules/outputs/types';
import type { TRunContentMatches } from './content-search';

export interface IRunsOutputColumnOptions {
  /** ListRuns `content_matches` of the loaded runs, keyed by run name. */
  contentMatches?: TRunContentMatches;
  /**
   * Whether any loaded run shows file-content search details under its start
   * time, which then needs room for "Matched in invoice.pdf".
   */
  showsContentSearchDetails?: boolean;
  /** Persisted output column widths, keyed by output name. */
  widths?: Record<string, number>;
  /** Persisted fixed column widths, keyed by column id. */
  columnWidths?: Record<string, number>;
  onOpenOutput?: (
    name: string,
    value: TResolvedValue,
    outputs: TResolvedOutputs,
  ) => void;
}

// Map of API filter values (PascalCase) to display strings
// Valid API values: Created, Pending, Executing, Stopped, NeedsWakeup,
// WaitingForTimer, NeedsGuidance, WaitingForGuidance, WaitingForBook, Completed
// Showing only few states for now
export const runStateKeys: Record<string, string> = {
  Pending: i18n.t('automation.runState.pending', { ns: 'automation' }),
  Executing: i18n.t('automation.runState.executing', { ns: 'automation' }),
  Stopped: i18n.t('automation.runState.stopped', { ns: 'automation' }),
  WaitingForGuidance: i18n.t('automation.runState.waitingForGuidance', {
    ns: 'automation',
  }),
  Completed: i18n.t('automation.runState.completed', { ns: 'automation' }),
} as const;

const INPUTS_COLUMN_WIDTH = 240;

/**
 * Returns column definitions for the automation runs list table.
 * Every output gets a column, in outputNames order. With no outputs at all, a
 * single "Outputs" column says so per run instead.
 *
 * @param handleViewRun - Callback function to handle the view run action
 * @param handleArchiveRun - Callback to archive a run
 * @param isArchiving - Whether an archive operation is in progress
 * @param outputNames - Every output the automation has, in display order
 * @param executionTimesMap - Map of run resource names to execution time data from the batch API
 * @param outputOptions - Persisted widths, the output preview handler and content matches
 * @returns Array of column definitions
 */
export const getRunsListColumns = (
  handleViewRun: (event: IDataTableRowClickedEvent<V1Run>) => void,
  handleArchiveRun: (run: V1Run) => void,
  isArchiving: boolean,
  outputNames: string[] = [],
  executionTimesMap: V1BatchGetRunExecutionTimesResponseRunExecutionTimes = {},
  outputOptions: IRunsOutputColumnOptions = {},
): IDataTableColDef<V1Run>[] => {
  // Widths depend only on the automation's outputs, never on which runs are on
  // the page, so paging can't reshuffle columns. Output columns share whatever
  // the fixed columns leave; without any, the empty-outputs cell takes it.
  // A column the user dragged keeps its width; the rest flex. If every output
  // was dragged, the last one still flexes so the leftover space has a home.
  const lastOutputName = outputNames.at(-1);
  const outputSizing = (outputName: string) => {
    const savedWidth = outputOptions.widths?.[outputName];
    if (savedWidth === undefined) {
      return { flex: 1, minWidth: OUTPUT_COLUMN_MIN_WIDTH };
    }
    const allOutputsSized = outputNames.every(
      name => outputOptions.widths?.[name] !== undefined,
    );
    return allOutputsSized && outputName === lastOutputName
      ? { flex: 1, minWidth: savedWidth }
      : { flex: 0, width: savedWidth, maxWidth: OUTPUT_COLUMN_MAX_WIDTH };
  };

  // A column the user dragged keeps that width on the next visit.
  const fixedWidth = (colId: string, defaultWidth: number) => ({
    colId,
    flex: 0,
    width: outputOptions.columnWidths?.[colId] ?? defaultWidth,
  });

  return [
    {
      headerName: i18n.t('automation.runs.startTime', { ns: 'automation' }),
      field: 'create_time',
      cellRenderer: createTimeCellRenderer(
        executionTimesMap,
        outputOptions.contentMatches,
      ),
      cellClass: 'run-time-cell',
      filter: false,
      sortable: false,
      resizable: true,
      // Fits the longest date, "September 30, 2026", without clipping, or
      // "12 of 120 files searchable" when content search details show.
      ...fixedWidth(
        RUNS_TABLE_FIXED_COLUMN_IDS.startTime,
        outputOptions.showsContentSearchDetails ? 216 : 168,
      ),
    },
    {
      headerName: i18n.t('automation.runs.status', { ns: 'automation' }),
      cellRenderer: StatusCellRenderer,
      filter: false,
      sortable: false,
      resizable: true,
      ...fixedWidth(RUNS_TABLE_FIXED_COLUMN_IDS.status, 100),
    },
    {
      headerName: i18n.t('automation.runs.version', { ns: 'automation' }),
      field: 'stage_version',
      cellRenderer: VersionCellRenderer,
      filter: false,
      sortable: false,
      resizable: true,
      ...fixedWidth(RUNS_TABLE_FIXED_COLUMN_IDS.version, 96),
      minWidth: 64,
    },
    {
      headerName: i18n.t('automation.runs.trigger', { ns: 'automation' }),
      cellRenderer: TriggerCellRenderer,
      filter: false,
      sortable: false,
      resizable: true,
      // Fits "by <first> <last>" under the trigger badge without clipping.
      ...fixedWidth(RUNS_TABLE_FIXED_COLUMN_IDS.trigger, 160),
    },
    {
      headerName: i18n.t('automation.runs.inputs', { ns: 'automation' }),
      cellRenderer: createInputsCellRenderer(),
      ...fixedWidth(RUNS_TABLE_FIXED_COLUMN_IDS.inputs, INPUTS_COLUMN_WIDTH),
      resizable: true,
      sortable: false,
      filter: false,
    },
    ...outputNames.map((outputName, index) => {
      // A divider separates Inputs from the outputs section.
      const hasDivider = index === 0;
      return {
        headerName: formatDisplayName(outputName),
        headerComponent: OutputColumnHeader,
        cellRenderer: createOutputCellRenderer(
          outputName,
          outputOptions.onOpenOutput,
          {
            capToDefaultWidth: outputOptions.widths?.[outputName] === undefined,
          },
        ),
        colId: `output:${outputName}`,
        ...outputSizing(outputName),
        // `!` because AG Grid's unlayered cell styles beat Tailwind's layer.
        // Previews own their inset, so drop the padding other cells get.
        cellClass: hasDivider
          ? 'runs-output-cell !p-0 !border-l !border-l-border'
          : 'runs-output-cell !p-0',
        resizable: true,
        sortable: false,
        filter: false,
        ...(hasDivider && { headerClass: '!border-l !border-l-border' }),
      };
    }),
    ...(outputNames.length === 0
      ? [
          {
            headerName: i18n.t('automation.runs.outputs.header', {
              ns: 'automation',
            }),
            cellRenderer: NoOutputsCellRenderer,
            cellClass: '!border-l !border-l-border',
            headerClass: '!border-l !border-l-border',
            // Takes the spare width like an output column, until dragged.
            colId: RUNS_TABLE_FIXED_COLUMN_IDS.outputs,
            ...(outputOptions.columnWidths?.[
              RUNS_TABLE_FIXED_COLUMN_IDS.outputs
            ] === undefined
              ? { flex: 1, minWidth: OUTPUT_COLUMN_MIN_WIDTH }
              : {
                  flex: 0,
                  width:
                    outputOptions.columnWidths[
                      RUNS_TABLE_FIXED_COLUMN_IDS.outputs
                    ],
                }),
            resizable: true,
            sortable: false,
            filter: false,
          },
        ]
      : []),
    {
      headerName: '',
      resizable: true,
      sortable: false,
      filter: false,
      // Hug the "View Run" button + actions menu. flex: 0 matters: without it
      // DataTable's default flex: 1 made this the column that soaked up spare
      // width.
      ...fixedWidth(RUNS_TABLE_FIXED_COLUMN_IDS.actions, 140),
      minWidth: 100,
      cellRenderer: createActionsCellRenderer(
        handleViewRun,
        handleArchiveRun,
        true,
        isArchiving,
      ),
      pinned: 'right',
    },
  ];
};

export const timePeriods = [
  {
    label: i18n.t('automation.runs.filter.last90days', { ns: 'automation' }),
    value: '90days',
  },
  {
    label: i18n.t('automation.runs.filter.last30days', { ns: 'automation' }),
    value: '30days',
  },
  {
    label: i18n.t('automation.runs.filter.last7days', { ns: 'automation' }),
    value: '7days',
  },
] as const;

export type TimePeriod = (typeof timePeriods)[number]['value'];
